import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';

interface CertificateItem {
  id: string;
  title: string;
  pdfPath: string;
}

const certificates: CertificateItem[] = [
  {
    id: 'haccp',
    title: 'Giấy chứng nhận HACCP Codex',
    pdfPath: '/CN CTY TRƯỜNG THỌ - SCAN.pdf',
  },
  {
    id: 'branch',
    title: 'Giấy phép đăng ký hoạt động chi nhánh',
    pdfPath: '/GP CN TRUONG THO.pdf',
  },
  {
    id: 'business',
    title: 'Giấy phép đăng ký doanh nghiệp',
    pdfPath: '/GPKD CTY TRUONG THO.pdf',
  },
];

const AllCertificates = () => {
  const [selectedCertificate, setSelectedCertificate] = useState<string | null>(
    null
  );

  const handleOpenModal = (certificateId: string) => {
    setSelectedCertificate(certificateId);
    document.body.style.overflow = 'hidden';
  };

  const handleCloseModal = () => {
    setSelectedCertificate(null);
    document.body.style.overflow = 'unset';
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Escape') {
      handleCloseModal();
    }
  };

  const selectedCert = certificates.find(
    cert => cert.id === selectedCertificate
  );

  return (
    <>
      <div className="w-full py-8 md:py-16 bg-gray3">
        <div className="container">
          {/* Section Title */}
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="flex flex-col items-center text-center gap-4 mb-8 md:mb-12"
          >
            <p className="uppercase text-gray2 text-[24px] md:text-[32px] font-bold">
              Giấy phép & Chứng nhận
            </p>
            <p className="text-[16px] md:text-[18px] font-medium text-gray-600 max-w-3xl">
              Các giấy phép và chứng nhận chính thức của công ty
            </p>
          </motion.div>

          {/* Certificates Grid */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8">
            {certificates.map((certificate, index) => (
              <motion.div
                key={certificate.id}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                className="flex flex-col gap-4"
              >
                {/* Certificate Preview Card */}
                <div
                  className="relative w-full h-[300px] md:h-[400px] rounded-2xl overflow-hidden shadow-xl group cursor-pointer border-4 border-white hover:border-primary/30 transition-all duration-300"
                  onClick={() => handleOpenModal(certificate.id)}
                  onKeyDown={e => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      handleOpenModal(certificate.id);
                    }
                  }}
                  role="button"
                  tabIndex={0}
                  aria-label={`Xem ${certificate.title} đầy đủ`}
                >
                  {/* PDF Preview */}
                  <object
                    data={`${certificate.pdfPath}#view=FitH&toolbar=0&navpanes=0`}
                    type="application/pdf"
                    className="w-full h-full pointer-events-none group-hover:scale-105 transition-transform duration-500"
                    aria-label={certificate.title}
                  >
                    <iframe
                      src={`${certificate.pdfPath}#view=FitH&toolbar=0&navpanes=0`}
                      className="w-full h-full pointer-events-none group-hover:scale-105 transition-transform duration-500"
                      title={certificate.title}
                    />
                  </object>

                  {/* Overlay on hover */}
                  <div className="absolute inset-0 bg-black/0 group-hover:bg-black/10 transition-all duration-300 flex items-center justify-center">
                    <div className="opacity-0 group-hover:opacity-100 transition-opacity duration-300 bg-white/90 backdrop-blur-sm rounded-full p-3 md:p-4">
                      <svg
                        className="w-6 h-6 md:w-8 md:h-8 text-primary"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0zM10 7v6m3-3H7"
                        />
                      </svg>
                    </div>
                  </div>

                  {/* Badge decoration */}
                  <div className="absolute top-3 right-3 w-12 h-12 md:w-16 md:h-16 bg-primary/90 backdrop-blur-sm rounded-full flex items-center justify-center shadow-lg">
                    <svg
                      className="w-6 h-6 md:w-8 md:h-8 text-white"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M9 12l2 2 4-4M7.835 4.697a3.42 3.42 0 001.946-.806 3.42 3.42 0 014.438 0 3.42 3.42 0 001.946.806 3.42 3.42 0 013.138 3.138 3.42 3.42 0 00.806 1.946 3.42 3.42 0 010 4.438 3.42 3.42 0 00-.806 1.946 3.42 3.42 0 01-3.138 3.138 3.42 3.42 0 00-1.946.806 3.42 3.42 0 01-4.438 0 3.42 3.42 0 00-1.946-.806 3.42 3.42 0 01-3.138-3.138 3.42 3.42 0 00-.806-1.946 3.42 3.42 0 010-4.438 3.42 3.42 0 00.806-1.946 3.42 3.42 0 013.138-3.138z"
                      />
                    </svg>
                  </div>
                </div>

                {/* Certificate Title */}
                <motion.div
                  initial={{ opacity: 0 }}
                  whileInView={{ opacity: 1 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: 0.3 + index * 0.1 }}
                  className="text-center"
                >
                  <p className="text-[14px] md:text-[16px] font-semibold text-gray2 leading-relaxed">
                    {certificate.title}
                  </p>
                </motion.div>
              </motion.div>
            ))}
          </div>
        </div>
      </div>

      {/* Modal for Full Certificate View */}
      <AnimatePresence>
        {selectedCertificate && selectedCert && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/90 backdrop-blur-sm"
            onClick={handleCloseModal}
            onKeyDown={handleKeyDown}
            tabIndex={-1}
          >
            {/* Close Button */}
            <button
              onClick={handleCloseModal}
              className="absolute top-4 right-4 md:top-8 md:right-8 z-10 w-12 h-12 rounded-full bg-white/10 backdrop-blur-sm hover:bg-white/20 transition-all duration-300 flex items-center justify-center group"
              aria-label="Đóng"
            >
              <svg
                className="w-6 h-6 text-white group-hover:rotate-90 transition-transform duration-300"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </button>

            {/* PDF Container */}
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              transition={{ duration: 0.3 }}
              className="relative w-full h-full max-w-6xl max-h-[90vh] bg-white rounded-2xl overflow-hidden shadow-2xl"
              onClick={e => e.stopPropagation()}
            >
              <object
                data={`${selectedCert.pdfPath}#view=FitH`}
                type="application/pdf"
                className="w-full h-full"
                aria-label={`${selectedCert.title} đầy đủ`}
              >
                <iframe
                  src={`${selectedCert.pdfPath}#view=FitH`}
                  className="w-full h-full"
                  title={`${selectedCert.title} đầy đủ`}
                />
              </object>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default AllCertificates;

